import { useRegisterCompany } from "@/api/companies/useRegisterCompany";
import { Button } from "@/components/ui/button";
import { handleFormErrors } from "@/lib/handleFormErrors";
import toast from "@/lib/toast";
import { RegisterCompany } from "@/schema/RegisterCompany";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface RegisterCompanyActionButtonsProps {
  turnstileToken: string | null;
  isTurnstileReady: boolean;
  isTurnstileLoading: boolean;
  resetTurnstile: () => void;
}

const RegisterCompanyActionButtons = ({
  turnstileToken,
  isTurnstileReady,
  isTurnstileLoading,
  resetTurnstile,
}: RegisterCompanyActionButtonsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleSubmit, setError, setValue } =
    useFormContext<RegisterCompany>();

  const { trigger: registerCompany, isMutating } = useRegisterCompany({
    onSuccess: (data) => {
      toast.success(data.message);
      navigate("/app/login");
    },
    onError: (error) => {
      handleFormErrors(error.errors, setError);
    },
  });

  const handleRegister = async (data: RegisterCompany) => {
    if (!data.agree_to_terms) {
      setError("agree_to_terms", {
        type: "manual",
        message: t("agree-to-terms-required"),
      });
      return;
    }
    if (data.user_password !== data.user_password_confirmation) {
      setError("user_password_confirmation", {
        type: "manual",
        message: t("password-confirmation-mismatch"),
      });
      return;
    }

    if (!turnstileToken) {
      toast.error(t("turnstile-verification-failed"));
      return;
    }

    toast.loading(t("register-company-loading"));
    setValue("cf_turnstile_response", turnstileToken, {
      shouldValidate: true,
      shouldDirty: true,
    });

    try {
      await registerCompany({
        ...data,
        cf_turnstile_response: turnstileToken,
      });
    } finally {
      resetTurnstile();
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[920px] justify-center px-6 pb-12 pt-4">
      <Button
        isLoading={isMutating}
        onClick={handleSubmit(handleRegister)}
        disabled={
          isMutating || !turnstileToken || !isTurnstileReady || isTurnstileLoading
        }
      >
        {t("register")}
      </Button>
    </div>
  );
};

export default RegisterCompanyActionButtons;
